/**
 * PeoplePage — DIAS-ready workforce hub.
 * Tabs: Staff roster · Licensing register · SOP assignments · Onboarding.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Users, UserPlus, ShieldCheck, ClipboardList, GraduationCap, Sparkles,
  Pencil, Trash2, X, Save, RefreshCw, Loader2, AlertTriangle, Stethoscope,
  ShieldAlert,
} from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCENT = '#3fa758';
const DARK = '#1f4a2e';

const CLINICAL_ROLE_OPTIONS = [
  { value: 'practice_manager',     label: 'Practice manager' },
  { value: 'radiologist',          label: 'Radiologist' },
  { value: 'radiographer',         label: 'Radiographer' },
  { value: 'sonographer',          label: 'Sonographer' },
  { value: 'nurse',                label: 'Nurse' },
  { value: 'reception',            label: 'Reception / Front desk' },
  { value: 'administrative_staff', label: 'Administrative staff' },
  { value: 'NOT_CLINICAL_STAFF',   label: 'Not a clinical staff member (hide from DIAS)' },
];

const APP_ROLE_OPTIONS = ['admin', 'superuser', 'radiologist', 'radiographer', 'sonographer', 'nursing', 'reception', 'trainee'];
const APP_ROLE_BADGE = {
  admin:        { bg: '#FEE2E2', fg: '#B91C1C' }, superuser:    { bg: '#CFFAFE', fg: '#0E7490' },
  radiologist:  { bg: '#E5E7EB', fg: '#374151' }, radiographer: { bg: '#E0E7FF', fg: '#4338CA' },
  sonographer:  { bg: '#DBEAFE', fg: '#1D4ED8' }, nursing:      { bg: '#FCE7F3', fg: '#BE185D' },
  reception:    { bg: '#FEF3C7', fg: '#92400E' }, trainee:      { bg: '#F3E8FF', fg: '#6B21A8' },
};

const today = () => new Date().toISOString().slice(0, 10);
const daysUntil = (iso) => { if (!iso) return null; return Math.round((new Date(iso) - new Date(today())) / 86400000); };

// External registry lookups — surfaced next to each licensing field and as a
// quick-link toolbar at the top of the Licensing Register tab. Update here
// if a regulator changes their public-register URL.
const REGISTRY_LOOKUPS = {
  ahpra: { label: 'AHPRA',   url: 'https://www.ahpra.gov.au/',                                                                                       hint: 'Public register of registered health practitioners (radiologists, radiographers, nurses).' },
  asar:  { label: 'ASAR',    url: 'https://www.asar.com.au/find-a-sonographer/',                                                                     hint: 'Australasian Sonographer Accreditation Registry — find an accredited sonographer.' },
  epa:   { label: 'NSW EPA', url: 'https://nswepa-rst-prod1.pegacloud.net/prweb/PRSExt/app/public-register-search/6WNoKA_CO_C-2gC8GOWKWg*/!STANDARD?Regulatory=Radiation', hint: 'NSW EPA public register — radiation user licences and management licences.' },
};

function VerifyLink({ which, color }) {
  const r = REGISTRY_LOOKUPS[which];
  if (!r) return null;
  return (
    <a href={r.url} target="_blank" rel="noreferrer"
       data-testid={`verify-${which}-link`}
       title={r.hint}
       className="text-[11px] font-semibold ml-2"
       style={{ color: color || '#0E7490' }}>
      Verify on {r.label} →
    </a>
  );
}

function StatusChip({ dateStr }) {
  if (!dateStr) return <span className="text-[10px] text-muted-foreground">—</span>;
  const d = daysUntil(dateStr);
  let color = '#16A34A', label = 'OK';
  if (d == null) { color = '#94a3b8'; label = '—'; }
  else if (d < 0) { color = '#DC2626'; label = `Expired ${Math.abs(d)}d`; }
  else if (d <= 30) { color = '#D97706'; label = `${d}d left`; }
  else if (d <= 90) { color = '#0EA5E9'; label = `${d}d`; }
  return <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ backgroundColor: `${color}15`, color }}>{label}</span>;
}

const clinicalRoleLabel = (v) => CLINICAL_ROLE_OPTIONS.find(o => o.value === v)?.label || v || '—';

export default function PeoplePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const [tab, setTab] = useState('roster');
  const [users, setUsers] = useState([]);
  const [licensing, setLicensing] = useState({ summary: {}, users: [] });
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => { if (!isPM) navigate('/'); }, [isPM, navigate]);

  const refresh = async () => {
    setLoading(true); setErr(null);
    try {
      const [u, l, a] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/users/licensing/expiring?within_days=60`, { withCredentials: true }).catch(() => ({ data: { summary: {}, users: [] } })),
        axios.get(`${API}/sops/assignments`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setUsers(u.data || []);
      setLicensing(l.data || { summary: {}, users: [] });
      setAssignments(Array.isArray(a.data) ? a.data : []);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const stats = useMemo(() => ({
    total: users.length,
    active: users.filter(u => u.active !== false).length,
    clinical: users.filter(u => u.clinical_role && u.clinical_role !== 'NOT_CLINICAL_STAFF').length,
    charterAccepted: users.filter(u => u.culture_charter_accepted_at).length,
  }), [users]);

  const tabs = [
    { id: 'roster',     l: 'Staff roster',       Icon: Users },
    { id: 'licensing',  l: 'Licensing register', Icon: ShieldCheck },
    { id: 'sops',       l: 'SOP assignments',    Icon: ClipboardList },
    { id: 'onboarding', l: 'Onboarding',         Icon: GraduationCap },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="people-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3" style={{ backgroundColor: `${ACCENT}1A`, color: ACCENT }}>
            <Users className="w-3.5 h-3.5" />
            <span className="text-[10px] font-bold uppercase tracking-wider">People · DIAS workforce</span>
          </div>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ color: DARK }}>
            Staff &amp; <span style={{ color: ACCENT }}>licensing</span>
          </h1>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
            Single source of truth for everyone who logs in — app permissions, clinical role for DIAS, expiry tracking, SOP assignments, and audit-ready compliance.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { l: 'Total staff', v: stats.total, Icon: Users },
            { l: 'Active', v: stats.active, Icon: ShieldCheck },
            { l: 'Clinical (DIAS)', v: stats.clinical, Icon: Stethoscope },
            { l: 'Charter accepted', v: stats.charterAccepted, Icon: ShieldAlert },
          ].map((s) => (
            <div key={s.l} className="bg-card border border-border rounded-xl p-3.5">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{s.l}</span>
                <s.Icon className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
              <div className="text-2xl font-bold" style={{ color: DARK }}>{s.v}</div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
          <div className="flex items-center gap-1 flex-wrap" data-testid="people-tabs">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`people-tab-${t.id}`}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 transition border ${tab === t.id ? 'text-white border-transparent' : 'text-muted-foreground border-border hover:bg-muted'}`}
                style={tab === t.id ? { backgroundColor: ACCENT } : {}}>
                <t.Icon className="w-3 h-3" /> {t.l}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="people-refresh" onClick={refresh} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl border border-border hover:bg-muted">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button data-testid="people-invite-create" onClick={() => setInviteOpen(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-xl"
              style={{ backgroundColor: ACCENT }}>
              <UserPlus className="w-4 h-4" /> Invite / create
            </button>
          </div>
        </div>

        {err && <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-600"><AlertTriangle className="w-3.5 h-3.5 inline mr-1" />{err}</div>}

        {loading ? <div className="bg-card border border-border rounded-2xl p-8 text-center text-sm text-muted-foreground">Loading…</div> : (
          <>
            {tab === 'roster' && <StaffRosterTab users={users} currentUserId={user?.id} onEdit={setEditingUser} onChange={refresh} />}
            {tab === 'licensing' && <LicensingRegisterTab licensing={licensing} onEdit={setEditingUser} />}
            {tab === 'sops' && <SOPAssignmentsTab assignments={assignments} users={users} />}
            {tab === 'onboarding' && <OnboardingTab users={users} />}
          </>
        )}
      </div>

      {inviteOpen && <InviteCreateModal accent={ACCENT} onClose={() => setInviteOpen(false)} onCreated={refresh} />}
      {editingUser && <EditStaffModal user={editingUser} accent={ACCENT} dark={DARK} onClose={() => setEditingUser(null)} onSaved={() => { setEditingUser(null); refresh(); }} />}
    </div>
  );
}

function StaffRosterTab({ users, currentUserId, onEdit, onChange }) {
  const [confirmDel, setConfirmDel] = useState(null);
  const sorted = useMemo(() => [...users].sort((a, b) => (a.name || '').localeCompare(b.name || '')), [users]);

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="staff-roster">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 text-left text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
              <th className="py-3 px-4">Staff</th><th className="py-3 px-3">App role</th>
              <th className="py-3 px-3">Clinical role</th><th className="py-3 px-3 text-center">2FA</th>
              <th className="py-3 px-3 text-center">Charter</th><th className="py-3 px-3">Status</th>
              <th className="py-3 px-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sorted.map((u) => {
              const isMe = String(u.id) === String(currentUserId);
              const badge = APP_ROLE_BADGE[u.role] || { bg: '#E5E7EB', fg: '#374151' };
              const isHidden = u.clinical_role === 'NOT_CLINICAL_STAFF' || !u.clinical_role;
              return (
                <tr key={u.id} data-testid={`staff-row-${u.email}`} className="hover:bg-muted/30">
                  <td className="py-3 px-4"><div className="font-semibold text-foreground">{u.name || '—'}</div><div className="text-xs text-muted-foreground">{u.email}</div></td>
                  <td className="py-3 px-3"><span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded" style={{ backgroundColor: badge.bg, color: badge.fg }}>{u.role}</span></td>
                  <td className="py-3 px-3"><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded inline-flex items-center gap-1 ${isHidden ? 'bg-slate-100 text-slate-500' : ''}`}
                    style={isHidden ? {} : { backgroundColor: `${ACCENT}1A`, color: ACCENT }}>
                    {!isHidden && <Stethoscope className="w-2.5 h-2.5" />}
                    {isHidden ? 'Hidden — DIAS' : clinicalRoleLabel(u.clinical_role)}
                  </span></td>
                  <td className="py-3 px-3 text-center">{u.totp_enabled ? <span className="text-emerald-600">✓</span> : <span className="text-slate-400">✗</span>}</td>
                  <td className="py-3 px-3 text-center">{u.culture_charter_accepted_at ? <span className="text-emerald-600">✓</span> : <span className="text-slate-400">✗</span>}</td>
                  <td className="py-3 px-3"><span className={`text-[10px] font-bold uppercase ${u.active === false ? 'text-slate-400' : 'text-emerald-600'}`}>{u.active === false ? 'Inactive' : 'Active'}</span></td>
                  <td className="py-3 px-3"><div className="flex items-center justify-end gap-1.5">
                    {isMe && <span className="text-[10px] text-muted-foreground italic">you</span>}
                    <button onClick={() => onEdit(u)} data-testid={`staff-edit-${u.email}`} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded border border-border hover:bg-muted"><Pencil className="w-3 h-3" /> Edit</button>
                    {!isMe && <button onClick={() => setConfirmDel(u)} data-testid={`staff-delete-${u.email}`} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded border border-red-500/30 text-red-600 hover:bg-red-500/5"><Trash2 className="w-3 h-3" /> Delete</button>}
                  </div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {confirmDel && <ConfirmDeleteModal user={confirmDel} onClose={() => setConfirmDel(null)} onDeleted={() => { setConfirmDel(null); onChange(); }} />}
    </div>
  );
}

function LicensingRegisterTab({ licensing, onEdit }) {
  const rows = licensing.users || [];
  const summary = licensing.summary || {};
  return (
    <div className="space-y-3" data-testid="licensing-register">
      {/* Quick registry-lookup toolbar — opens each public register in a new tab */}
      <div className="bg-card border border-border rounded-xl p-3.5 flex flex-col sm:flex-row sm:items-center gap-3"
           data-testid="registry-lookups-toolbar">
        <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-muted-foreground flex-none">
          Verify a practitioner
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(REGISTRY_LOOKUPS).map(([k, r]) => (
            <a key={k} href={r.url} target="_blank" rel="noreferrer"
               data-testid={`registry-lookup-${k}`}
               title={r.hint}
               className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border hover:bg-muted transition-colors"
               style={{ borderColor: '#cbd5e1', color: DARK }}>
              {r.label} <span className="text-muted-foreground">→</span>
            </a>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { k: 'expired', l: 'Expired', c: '#DC2626' },
          { k: 'expiring_soon', l: 'Expiring ≤60d', c: '#D97706' },
          { k: 'current', l: 'Current', c: '#16A34A' },
          { k: 'missing', l: 'Missing entry', c: '#6B7280' },
        ].map(t => (
          <div key={t.k} className="bg-card border border-border rounded-xl p-3.5">
            <div className="text-[10px] uppercase tracking-wider font-semibold mb-1" style={{ color: t.c }}>{t.l}</div>
            <div className="text-2xl font-bold" style={{ color: DARK }}>{summary[t.k] || 0}</div>
          </div>
        ))}
      </div>
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 text-left text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                <th className="py-3 px-4">Staff</th><th className="py-3 px-3">Role</th>
                <th className="py-3 px-3">AHPRA</th><th className="py-3 px-3">ASAR</th>
                <th className="py-3 px-3">Radiation</th><th className="py-3 px-3">BLS / CPR</th>
                <th className="py-3 px-3">Contrast</th><th className="py-3 px-3">CPD YTD</th>
                <th className="py-3 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-sm text-muted-foreground">No clinical staff yet — set someone's clinical role to populate this register.</td></tr>}
              {rows.map(r => (
                <tr key={r.user_id} data-testid={`licensing-row-${r.email}`} className="hover:bg-muted/30">
                  <td className="py-3 px-4"><div className="font-semibold">{r.name}</div><div className="text-xs text-muted-foreground">{r.email}</div></td>
                  <td className="py-3 px-3 text-xs">{clinicalRoleLabel(r.clinical_role)}</td>
                  <td className="py-3 px-3"><div className="text-xs">{r.ahpra_number || '—'}</div><StatusChip dateStr={r.ahpra_expiry} /></td>
                  <td className="py-3 px-3"><div className="text-xs">{r.asar_number || '—'}</div><StatusChip dateStr={r.asar_expiry} /></td>
                  <td className="py-3 px-3"><div className="text-xs">{r.radiation_licence_no || '—'}</div><StatusChip dateStr={r.radiation_licence_expiry} /></td>
                  <td className="py-3 px-3"><StatusChip dateStr={r.bls_expiry} /></td>
                  <td className="py-3 px-3"><StatusChip dateStr={r.contrast_accreditation_expiry} /></td>
                  <td className="py-3 px-3 text-xs">{r.cpd_hours_ytd || 0}h <span className="text-muted-foreground">({r.cpd_year || '—'})</span></td>
                  <td className="py-3 px-3 text-right">
                    <button onClick={() => onEdit({ id: r.user_id, name: r.name, email: r.email, role: r.role, clinical_role: r.clinical_role, licensing: r })}
                      className="text-xs font-semibold inline-flex items-center gap-1 px-2.5 py-1 rounded border border-border hover:bg-muted">
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SOPAssignmentsTab({ assignments, users }) {
  const userById = useMemo(() => Object.fromEntries(users.map(u => [u.id, u])), [users]);
  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="sop-assignments">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-muted/50 text-left text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
            <th className="py-3 px-4">Staff</th><th className="py-3 px-3">SOP</th>
            <th className="py-3 px-3">Due</th><th className="py-3 px-3">Completed</th>
            <th className="py-3 px-3">Status</th>
          </tr></thead>
          <tbody className="divide-y divide-border">
            {assignments.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-sm text-muted-foreground">No SOP assignments yet.</td></tr>}
            {assignments.map((a, i) => {
              const u = userById[a.user_id] || {};
              const overdue = a.status !== 'completed' && a.due_date && new Date(a.due_date) < new Date(today());
              return (
                <tr key={a.assignment_id || i}>
                  <td className="py-3 px-4 text-xs">{u.name || a.user_id}</td>
                  <td className="py-3 px-3 text-xs font-mono">{a.sop_id}</td>
                  <td className="py-3 px-3 text-xs">{a.due_date || '—'}</td>
                  <td className="py-3 px-3 text-xs">{a.completed_at?.slice(0, 10) || '—'}</td>
                  <td className="py-3 px-3"><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${a.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : overdue ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{a.status === 'completed' ? 'Completed' : overdue ? 'Overdue' : 'Pending'}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OnboardingTab({ users }) {
  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="onboarding-progress">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-muted/50 text-left text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
            <th className="py-3 px-4">Staff</th><th className="py-3 px-3 text-center">Active</th>
            <th className="py-3 px-3 text-center">2FA</th><th className="py-3 px-3 text-center">Charter</th>
            <th className="py-3 px-3 text-center">Clinical role</th>
          </tr></thead>
          <tbody className="divide-y divide-border">
            {users.map(u => (
              <tr key={u.id}>
                <td className="py-3 px-4"><div className="font-semibold">{u.name}</div><div className="text-xs text-muted-foreground">{u.email}</div></td>
                <td className="py-3 px-3 text-center">{u.active !== false ? '✅' : '—'}</td>
                <td className="py-3 px-3 text-center">{u.totp_enabled ? '✅' : '⏳'}</td>
                <td className="py-3 px-3 text-center">{u.culture_charter_accepted_at ? '✅' : '⏳'}</td>
                <td className="py-3 px-3 text-center">{u.clinical_role && u.clinical_role !== 'NOT_CLINICAL_STAFF' ? '✅' : (u.clinical_role === 'NOT_CLINICAL_STAFF' ? '➖' : '⏳')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EditStaffModal({ user, accent, dark, onClose, onSaved }) {
  const lic = user.licensing || {};
  const [form, setForm] = useState({
    name: user.name || '',
    clinical_role: user.clinical_role || 'NOT_CLINICAL_STAFF',
    active: user.active !== false,
    ahpra_number: lic.ahpra_number || '', ahpra_expiry: lic.ahpra_expiry || '',
    asar_number: lic.asar_number || '', asar_expiry: lic.asar_expiry || '',
    radiation_licence_no: lic.radiation_licence_no || '', radiation_licence_expiry: lic.radiation_licence_expiry || '',
    bls_expiry: lic.bls_expiry || '', contrast_accreditation_expiry: lic.contrast_accreditation_expiry || '',
    accreditations: (lic.accreditations || []).join(', '),
    cpd_hours_ytd: lic.cpd_hours_ytd || 0,
    cpd_year: lic.cpd_year || new Date().getFullYear(),
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      if (form.name && form.name !== user.name)
        await axios.patch(`${API}/users/${user.id}/profile`, { name: form.name }, { withCredentials: true });
      if (form.clinical_role !== user.clinical_role)
        await axios.patch(`${API}/users/${user.id}/clinical-role`, { clinical_role: form.clinical_role }, { withCredentials: true });
      if (form.active && user.active === false)
        await axios.put(`${API}/users/${user.id}/activate`, null, { withCredentials: true });
      else if (!form.active && user.active !== false)
        await axios.delete(`${API}/users/${user.id}`, { withCredentials: true });
      await axios.patch(`${API}/users/${user.id}/licensing`, {
        ahpra_number: form.ahpra_number, ahpra_expiry: form.ahpra_expiry,
        asar_number: form.asar_number, asar_expiry: form.asar_expiry,
        radiation_licence_no: form.radiation_licence_no, radiation_licence_expiry: form.radiation_licence_expiry,
        bls_expiry: form.bls_expiry, contrast_accreditation_expiry: form.contrast_accreditation_expiry,
        accreditations: form.accreditations.split(',').map(s => s.trim()).filter(Boolean),
        cpd_hours_ytd: parseFloat(form.cpd_hours_ytd) || 0,
        cpd_year: parseInt(form.cpd_year, 10) || 0,
      }, { withCredentials: true });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="edit-staff-modal">
      <div className="absolute inset-0 bg-black/50" onClick={busy ? undefined : onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto p-6 shadow-2xl space-y-5">
        <div className="flex items-start justify-between gap-3 pb-3 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${accent}20` }}><Pencil className="w-5 h-5" style={{ color: accent }} /></div>
            <div><h3 className="font-['Outfit'] text-lg font-bold" style={{ color: dark }}>Edit staff member</h3><p className="text-xs text-muted-foreground">{user.email}</p></div>
          </div>
          <button onClick={onClose} disabled={busy}><X className="w-5 h-5" /></button>
        </div>
        {err && <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-2.5 text-xs text-red-600">{err}</div>}

        <section className="space-y-3">
          <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">Profile</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field l="Display name"><input data-testid="edit-name" value={form.name} onChange={e => setF('name', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Field>
            <Field l="App role (locked)"><input value={user.role} disabled className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-muted text-muted-foreground" /></Field>
          </div>
          <Field l="Clinical role">
            <select data-testid="edit-clinical-role" value={form.clinical_role} onChange={e => setF('clinical_role', e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border-2 bg-background" style={{ borderColor: accent }}>
              {CLINICAL_ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <p className="text-[10px] text-muted-foreground mt-0.5">Pick "Not a clinical staff member" to hide admin-only logins from DIAS reports.</p>
          </Field>
          {user.role !== 'admin' && (
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" data-testid="edit-active" checked={form.active} onChange={e => setF('active', e.target.checked)} className="w-4 h-4" style={{ accentColor: accent }} />
              <span>Active</span>
            </label>
          )}
        </section>

        <section className="space-y-3 border-t border-border pt-5">
          <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">Licensing &amp; certification</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field l={<span className="flex items-center justify-between"><span>AHPRA registration #</span><VerifyLink which="ahpra" color={accent} /></span>}>
              <input data-testid="edit-ahpra-no" placeholder="MED0001234567" value={form.ahpra_number} onChange={e => setF('ahpra_number', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" />
            </Field>
            <Field l="AHPRA expiry"><input type="date" data-testid="edit-ahpra-expiry" value={form.ahpra_expiry} onChange={e => setF('ahpra_expiry', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Field>
            <Field l={<span className="flex items-center justify-between"><span>ASAR # (sonographers)</span><VerifyLink which="asar" color={accent} /></span>}>
              <input data-testid="edit-asar-no" value={form.asar_number} onChange={e => setF('asar_number', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" />
            </Field>
            <Field l="ASAR expiry"><input type="date" value={form.asar_expiry} onChange={e => setF('asar_expiry', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Field>
            <Field l={<span className="flex items-center justify-between"><span>Radiation licence #</span><VerifyLink which="epa" color={accent} /></span>}>
              <input data-testid="edit-radiation-no" value={form.radiation_licence_no} onChange={e => setF('radiation_licence_no', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" />
            </Field>
            <Field l="Radiation licence expiry"><input type="date" value={form.radiation_licence_expiry} onChange={e => setF('radiation_licence_expiry', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Field>
            <Field l="BLS / CPR expiry"><input type="date" value={form.bls_expiry} onChange={e => setF('bls_expiry', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Field>
            <Field l="Contrast accreditation expiry"><input type="date" value={form.contrast_accreditation_expiry} onChange={e => setF('contrast_accreditation_expiry', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Field>
          </div>
          <Field l={<>Other accreditations <span className="text-muted-foreground">(comma-separated)</span></>}>
            <input value={form.accreditations} onChange={e => setF('accreditations', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field l="CPD hours YTD"><input type="number" min="0" step="0.5" value={form.cpd_hours_ytd} onChange={e => setF('cpd_hours_ytd', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Field>
            <Field l="CPD reporting year"><input type="number" min="2020" max="2099" value={form.cpd_year} onChange={e => setF('cpd_year', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Field>
          </div>
        </section>

        <div className="flex items-center justify-end gap-2 pt-4 border-t border-border">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm font-semibold rounded-lg border border-border">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="edit-staff-save"
            className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-50"
            style={{ backgroundColor: accent }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ l, children }) {
  return <div><label className="block text-xs font-medium mb-1">{l}</label>{children}</div>;
}

function ConfirmDeleteModal({ user, onClose, onDeleted }) {
  const [confirmEmail, setConfirmEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async () => {
    setBusy(true); setErr(null);
    try { await axios.delete(`${API}/users/${user.id}/permanent`, { params: { confirm_email: confirmEmail }, withCredentials: true }); onDeleted(); }
    catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={busy ? undefined : onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-md p-5 space-y-3">
        <h3 className="font-['Outfit'] text-base font-bold flex items-center gap-2 text-red-600"><AlertTriangle className="w-4 h-4" /> Permanently delete {user.name}?</h3>
        <p className="text-xs text-muted-foreground">Type <strong className="text-foreground">{user.email}</strong> below to confirm.</p>
        <input value={confirmEmail} onChange={e => setConfirmEmail(e.target.value)} placeholder={user.email} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" data-testid="confirm-delete-email" />
        {err && <div className="text-xs text-red-600">{err}</div>}
        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-border">Cancel</button>
          <button onClick={submit} disabled={busy || confirmEmail.toLowerCase().trim() !== (user.email || '').toLowerCase().trim()}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-semibold rounded-lg bg-red-600 text-white disabled:opacity-50" data-testid="confirm-delete-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Delete forever
          </button>
        </div>
      </div>
    </div>
  );
}

function InviteCreateModal({ accent, onClose, onCreated }) {
  const [mode, setMode] = useState('invite');
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'reception', sop_assignments: [] });
  const [sopsList, setSopsList] = useState([]);
  const [defaults, setDefaults] = useState({ bundles: {}, default_due_days: 14 });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    axios.get(`${API}/sops`, { withCredentials: true }).then(r => setSopsList(r.data || [])).catch(() => {});
    axios.get(`${API}/role-sop-defaults`, { withCredentials: true }).then(r => setDefaults(r.data || { bundles: {} })).catch(() => {});
  }, []);

  const setRole = (newRole) => {
    const ids = (defaults.bundles || {})[newRole] || [];
    const due = new Date(Date.now() + (defaults.default_due_days || 14) * 86400000).toISOString().slice(0, 10);
    setForm(f => ({ ...f, role: newRole, sop_assignments: ids.map(sop_id => ({ sop_id, due_date: due })) }));
  };

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const payload = { email: form.email, name: form.name, role: form.role, sop_assignments: form.sop_assignments };
      if (mode === 'invite') {
        const r = await axios.post(`${API}/users/invite`, payload, { withCredentials: true });
        setCreated(r.data);
      } else {
        if (form.password.length < 8) throw new Error('Password must be at least 8 characters.');
        await axios.post(`${API}/users/create`, { ...payload, password: form.password }, { withCredentials: true });
        onCreated(); onClose();
      }
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  if (created) {
    const link = `${window.location.origin}${created.invite_link}`;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="invite-success">
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-background border border-border rounded-2xl p-5 w-full max-w-lg space-y-3">
          <h3 className="font-['Outfit'] text-lg font-bold">Invite created</h3>
          <div className="flex items-center gap-2 bg-muted border border-border rounded-lg p-2">
            <input readOnly value={link} className="flex-1 bg-transparent text-xs font-mono outline-none" />
            <button onClick={() => navigator.clipboard.writeText(link)} className="text-xs font-semibold px-2.5 py-1 rounded border border-border">Copy</button>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button onClick={() => { setCreated(null); setForm({ email: '', name: '', password: '', role: 'reception', sop_assignments: [] }); }} className="text-sm px-3 py-1.5 rounded-lg border border-border">Create another</button>
            <button onClick={() => { onCreated(); onClose(); }} className="text-sm font-semibold px-4 py-1.5 rounded-lg text-white" style={{ backgroundColor: accent }}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="invite-create-modal">
      <div className="absolute inset-0 bg-black/50" onClick={busy ? undefined : onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-2xl p-5 shadow-2xl space-y-3">
        <div className="flex items-start justify-between gap-3 pb-3 border-b border-border">
          <h3 className="font-['Outfit'] text-lg font-bold">Add new staff member</h3>
          <button onClick={onClose} disabled={busy}><X className="w-4 h-4" /></button>
        </div>
        <div className="inline-flex items-center gap-1 bg-muted rounded-lg p-1">
          {[['invite', 'Invite link'], ['create', 'Direct create']].map(([id, label]) => (
            <button key={id} onClick={() => setMode(id)} data-testid={`invite-mode-${id}`}
              className={`text-xs font-semibold px-3 py-1.5 rounded transition ${mode === id ? 'text-white' : 'text-muted-foreground'}`}
              style={mode === id ? { backgroundColor: accent } : {}}>{label}</button>
          ))}
        </div>
        {err && <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-2.5 text-xs text-red-600">{err}</div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field l="Email"><input data-testid="invite-email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Field>
          <Field l="Display name"><input data-testid="invite-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Field>
          <Field l="App role"><select data-testid="invite-role" value={form.role} onChange={e => setRole(e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background">
            {APP_ROLE_OPTIONS.filter(r => r !== 'admin').map(r => <option key={r} value={r}>{r}</option>)}
          </select></Field>
          {mode === 'create' && <Field l="Password"><input data-testid="invite-password" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Field>}
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button onClick={onClose} disabled={busy} className="text-sm px-3 py-1.5 rounded-lg border border-border">Cancel</button>
          <button onClick={submit} disabled={busy || !form.email || !form.name} data-testid="invite-submit"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-5 py-2 rounded-lg text-white disabled:opacity-50"
            style={{ backgroundColor: accent }}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
            {mode === 'invite' ? 'Generate invite link' : 'Create account'}
          </button>
        </div>
      </div>
    </div>
  );
}
